import { GooglePlaceTypesMap } from '@/models/locationModel';

export type GtCategory = 'eat' | 'stay' | 'do';

/** Place-like input. Fields are `unknown` so both Location classes and raw slot snapshots pass. */
export interface GtCategorySource {
  gt_category?: unknown;
  google_types?: unknown;
  google_places_data?: unknown;
}

export function isGtCategory(value: unknown): value is GtCategory {
  return value === 'eat' || value === 'stay' || value === 'do';
}

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is string => typeof item === 'string');
}

export function googleTypesFrom(source: GtCategorySource | null | undefined): string[] {
  if (!source) {
    return [];
  }
  const direct = readStringArray(source.google_types);
  if (direct.length > 0) {
    return direct;
  }
  const placesData = source.google_places_data;
  if (placesData && typeof placesData === 'object') {
    return readStringArray((placesData as { types?: unknown }).types);
  }
  return [];
}

/**
 * Same rules as the Location constructor: featured eat, then featured stay, otherwise do.
 * Cities (`locality`) and attractions that are not eat or stay land on do.
 */
export function gtCategoryFromGoogleTypes(types: readonly string[]): GtCategory {
  if (types.some((type) => GooglePlaceTypesMap.FEATURED_EAT.includes(type))) {
    return 'eat';
  }
  if (types.some((type) => GooglePlaceTypesMap.FEATURED_STAY.includes(type))) {
    return 'stay';
  }
  return 'do';
}

/**
 * Category for a slot or place modal.
 * Google types win when present, so a museum saved as `eat` or as a raw type string
 * still resolves to do. Otherwise a stored eat/stay/do is kept. A fallback slot type
 * is used only when it is already eat, stay, or do (not `museum` or `category_name`).
 */
export function resolveGtCategory(
  source: GtCategorySource | null | undefined,
  fallbackSlotType?: unknown,
): GtCategory {
  const types = googleTypesFrom(source);
  if (types.length > 0) {
    return gtCategoryFromGoogleTypes(types);
  }
  if (isGtCategory(source?.gt_category)) {
    return source.gt_category;
  }
  if (isGtCategory(fallbackSlotType)) {
    return fallbackSlotType;
  }
  return 'do';
}
